import Papa from 'papaparse';

import type { BulkLeftCompanyEmployee } from '../../Services';
import { NAMED_HEADERS } from './types';
import type { ParseResult, RowError } from './types';
import { isValidUuid, isNumeric, toBackendDate } from './utils';

function isRowEffectivelyEmpty(values: string[]): boolean {
  return values.every(v => v.trim() === '');
}

export function parseCsv(file: File): Promise<ParseResult> {
  return new Promise(resolve => {
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: false, // we handle empty rows ourselves
      complete(results) {
        const fields = results.meta.fields ?? [];
        const hasNamedHeaders = NAMED_HEADERS.every(h => fields.includes(h));
        const columnCount = fields.length;

        // ── Structural validation ──────────────────────────────────────────
        if (!hasNamedHeaders) {
          if (columnCount !== 4) {
            return resolve({
              ok: false,
              errors: [{ kind: 'badColumnCount', count: columnCount }],
            });
          }
        }

        const employees: BulkLeftCompanyEmployee[] = [];
        const rowErrors: RowError[] = [];

        results.data.forEach((rawRow, idx) => {
          const rowNumber = idx + 2; // 1-based, +1 for header

          let connectionId: string;
          let bsn: string;
          let startDateRaw: string;
          let endDateRaw: string;

          if (hasNamedHeaders) {
            connectionId = rawRow['connectionId']?.trim() ?? '';
            bsn = rawRow['bsn']?.trim() ?? '';
            startDateRaw = rawRow['startDate']?.trim() ?? '';
            endDateRaw = rawRow['endDate']?.trim() ?? '';
          } else {
            const values = Object.values(rawRow);
            // Skip fully-empty rows in positional mode
            if (isRowEffectivelyEmpty(values)) return;
            connectionId = values[0]?.trim() ?? '';
            bsn = values[1]?.trim() ?? '';
            startDateRaw = values[2]?.trim() ?? '';
            endDateRaw = values[3]?.trim() ?? '';
          }

          // Skip fully-empty rows in named-header mode
          if (!connectionId && !bsn && !startDateRaw && !endDateRaw) return;

          // ── Required field presence ────────────────────────────────────
          if (!connectionId) {
            rowErrors.push({ kind: 'missingField', row: rowNumber, field: 'connectionId' });
            return;
          }
          if (!bsn) {
            rowErrors.push({ kind: 'missingField', row: rowNumber, field: 'bsn' });
            return;
          }
          if (!endDateRaw) {
            rowErrors.push({ kind: 'missingField', row: rowNumber, field: 'endDate' });
            return;
          }

          // ── Field-level validation ────────────────────────────────────
          if (!isValidUuid(connectionId)) {
            rowErrors.push({ kind: 'invalidUuid', row: rowNumber, value: connectionId });
            return;
          }
          if (!isNumeric(bsn)) {
            rowErrors.push({ kind: 'invalidBsn', row: rowNumber, value: bsn });
            return;
          }

          const endDate = toBackendDate(endDateRaw);
          if (!endDate) {
            rowErrors.push({ kind: 'invalidDate', row: rowNumber, value: endDateRaw });
            return;
          }

          let startDate: string | null = null;
          if (startDateRaw) {
            startDate = toBackendDate(startDateRaw);
            if (!startDate) {
              rowErrors.push({ kind: 'invalidStartDate', row: rowNumber, value: startDateRaw });
              return;
            }
          }

          employees.push({ connectionId, bsn, startDate, endDate });
        });

        if (rowErrors.length > 0) {
          return resolve({ ok: false, errors: rowErrors });
        }

        resolve({ ok: true, employees });
      },
    });
  });
}
